/**
 * Auth API
 *
 * Client-side wrappers for authentication-related endpoints: sign in/up,
 * verification, password flows, status, logout, and account deletion.
 */
import { post, get, del } from '../core';

/**
 * Sign in a user with email and password
 * @param {Object} credentials - The user credentials
 * @returns {Promise<Object>} - The authentication response
 */
export function signIn(credentials) {
  return post('/auth/signin', credentials);
}

/**
 * Sign up a new user
 * @param {Object} userData - The new user data
 * @returns {Promise<Object>} - The registration response
 */
export function signUp(userData) {
  return post('/auth/signup', userData);
}

/**
 * Request a password reset
 * @param {Object} data - Object containing email
 * @returns {Promise<Object>} - The response
 */
export function forgotPassword(data) {
  return post('/auth/forgot-password', data);
}

/**
 * Reset a password with a token
 * @param {Object} data - Object containing token and new password
 * @returns {Promise<Object>} - The response
 */
export function resetPassword(data) {
  return post('/auth/reset-password', data);
}

/**
 * Verify an email address
 * @param {Object} data - Object containing verification token
 * @returns {Promise<Object>} - The response
 */
export function verifyEmail(data) {
  return post('/auth/verify-email', data);
}

/**
 * Get the current auth status
 * @returns {Promise<Object>} - The auth status
 */
export function getAuthStatus() {
  return get('/auth/status');
}

/**
 * Logout the current user on the server side
 * @returns {Promise<Object>} - The logout response
 */
export function logout() {
  return post('/auth/logout', {});
}

/**
 * Delete the current user's account
 * @returns {Promise<Object>} - The delete account response
 */
export function deleteUserAccount() {
  return del('/auth/account');
}

export default {
  signIn,
  signUp,
  forgotPassword,
  resetPassword,
  verifyEmail,
  getAuthStatus,
  logout,
  deleteUserAccount
}; 